import loadComponents from './components/index';
import loadBlocks from './blocks';
import constants from './constants';

export default function swiperSlider(editor, opts = {}) {
    const options = {
        ...{
            // Object to extend the default slider block, eg. `{ label: 'Container', attributes: { ... } }`
            // Pass a falsy value to avoid adding the block
            sliderBlock: {},

            // Object to extend the default slider properties, eg. `{ name: 'My Container', draggable: false, ... }`
            swiperProps: {},

            // Object to extend the default slider container properties
            containerProps: {},

            // Object to extend the default wrapper properties
            wrapperProps: {},

            // Object to extend the default slide properties
            slideProps: {},

            // Object to extend the default previous nav properties
            prevProps: {},

            // Object to extend the default next nav properties
            nextProps: {},

            // Object to extend the default pagination properties
            paginationProps: {},

            // Object to extend the default scrollbar properties
            scrollbarProps: {},

            // Default slides
            slideEls: `
              <div class="swiper-slide"></div>
              <div class="swiper-slide"></div>
              <div class="swiper-slide"></div>
            `,

            // Previous nav element string, as for example, an HTML string
            prevEl: `<div class="swiper-button-prev"></div>`,

            // Next nav element string, as for example, an HTML string
            nextEl: `<div class="swiper-button-next"></div>`,

            paginationEl: `<div class="swiper-pagination"></div>`,

            scrollbarEl: `<div class="swiper-scrollbar"></div>`,

            // Class name for the slider frame
            classContainer: 'swiper',

            // Class name for slides container
            classWrapper: 'swiper-wrapper',

            // Class name for slides container
            classSlide: 'swiper-slide',

            // Class name for slider previous control
            classPrev: 'swiper-button-prev',

            // Class name for slider next control
            classNext: 'swiper-button-next',

            classPagination: 'swiper-pagination',

            classScrollbar: 'swiper-scrollbar',

            // Script to load dynamically in case no Swiper instance was found
            script:
                'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js',

            // Style to load dynamically in case no Swiper instance was found
            style:
                'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.css',
        },
        ...opts,
    };
    const {
        containerName,
        wrapperName,
    } = constants;
    const navUi = {
        selectedClass: 'sio-swiper-editor-selected',
        barClass: 'sio-swiper-editor-topbar',
        paginationClass: 'sio-swiper-editor-topbar__pagination',
        numbersClass: 'sio-swiper-editor-topbar__numbers',
        statusClass: 'sio-swiper-editor-topbar__status',
        actionsClass: 'sio-swiper-editor-topbar__actions',
        buttonClass: 'sio-swiper-editor-topbar__btn',
        activeButtonClass: 'sio-swiper-editor-topbar__btn--active',
        arrowButtonClass: 'sio-swiper-editor-topbar__btn--arrow',
        moveButtonClass: 'sio-swiper-editor-topbar__btn--move',
        numberButtonClass: 'sio-swiper-editor-topbar__btn--number',
        controlButtonClass: 'sio-swiper-editor-topbar__btn--control',
        disabledButtonClass: 'sio-swiper-editor-topbar__btn--disabled',
    };

    const icons = {
        chevronLeft: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>',
        chevronRight: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>',
        arrowUp: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 7-7 7 7"/><path d="M12 19V5"/></svg>',
        arrowDown: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m19 12-7 7-7-7"/><path d="M12 5v14"/></svg>',
        plus: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>',
        minus: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/></svg>',
        duplicate: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>',
    };
    let selectedSwiperComponent = null;
    let selectedSlidesCollection = null;
    let selectedSlidesListener = null;
    let selectedSwiperInstance = null;
    let selectedSwiperSlideListener = null;
    let selectedSlideIndex = 0;
    let topBarPositionListener = null;
    let topBarDoc = null;

    const topBarHeight = 44;

    const positionTopBar = (sliderEl, topBar) => {
        if (!sliderEl || !topBar) return;
        const rect = sliderEl.getBoundingClientRect();
        const win = sliderEl.ownerDocument?.defaultView;
        const vw = win?.innerWidth ?? rect.width;
        const edgePad = 12;

        let width = Math.max(0, rect.width - 220);
        let left = rect.left;

        const maxRight = vw - edgePad;
        width = Math.min(width, Math.max(0, maxRight - left));
        if (left + width > maxRight) {
            left = Math.max(edgePad, maxRight - width);
        }

        topBar.style.top = `${rect.top - topBarHeight - 6}px`;
        topBar.style.left = `${left}px`;
        topBar.style.width = `${width}px`;
    };

    const clearTopBarPositionListener = () => {
        if (topBarPositionListener && topBarDoc) {
            topBarDoc.removeEventListener('scroll', topBarPositionListener, true);
            if (topBarDoc.defaultView) {
                topBarDoc.defaultView.removeEventListener('resize', topBarPositionListener);
            }
        }
        topBarPositionListener = null;
        topBarDoc = null;
    };

    const getSlidesWrapper = (component) => {
        if (!component) return null;
        return component
            .components()
            .find((comp) => comp.get('type') === wrapperName);
    };

    const getSlideCount = (component) => {
        const wrapper = getSlidesWrapper(component);
        if (!wrapper) return 0;
        return wrapper.components().length;
    };

    const getSlidesCollection = (component) => {
        const wrapper = getSlidesWrapper(component);
        return wrapper ? wrapper.components() : null;
    };

    const getSwiperInstance = (component) => {
        const sliderEl = component?.getEl?.();
        if (!sliderEl) return null;

        return (
            sliderEl.__sioSwiperInstance ||
            sliderEl.swiper ||
            sliderEl.ownerDocument?.defaultView?.sliderSwiper ||
            null
        );
    };

    const getCurrentSlideIndex = (component, totalSlides) => {
        if (!totalSlides) return 0;

        const swiper = getSwiperInstance(component);
        if (!swiper) return 0;

        let rawIndex = Number(swiper.realIndex);
        if (!Number.isFinite(rawIndex)) {
            rawIndex = Number(swiper.activeIndex);
        }
        if (!Number.isFinite(rawIndex)) return 0;

        return Math.max(0, Math.min(totalSlides - 1, rawIndex));
    };

    const getKnownSlideIndex = (component, totalSlides) => {
        if (!totalSlides) return 0;
        const sliderEl = component?.getEl?.();
        const fromDom = sliderEl?.dataset?.sioEditorActiveSlide;
        const fromDomIndex = Number.parseInt(fromDom, 10);
        if (Number.isFinite(fromDomIndex)) {
            return Math.max(0, Math.min(totalSlides - 1, fromDomIndex));
        }

        if (Number.isFinite(selectedSlideIndex)) {
            return Math.max(0, Math.min(totalSlides - 1, selectedSlideIndex));
        }

        return getCurrentSlideIndex(component, totalSlides);
    };

    const setKnownSlideIndex = (component, nextIndex) => {
        const totalSlides = getSlideCount(component);
        if (!totalSlides) {
            selectedSlideIndex = 0;
            return 0;
        }

        const clamped = Math.max(0, Math.min(totalSlides - 1, Number(nextIndex) || 0));
        selectedSlideIndex = clamped;
        const sliderEl = component?.getEl?.();
        if (sliderEl) {
            sliderEl.dataset.sioEditorActiveSlide = String(clamped);
        }
        return clamped;
    };

    const createNavButton = (
        doc,
        {
            label,
            icon,
            classes,
            title,
            onClick,
            disabled = false,
        }
    ) => {
        const button = doc.createElement('button');
        button.type = 'button';
        if (icon) {
            button.innerHTML = icon;
        } else {
            button.textContent = label;
        }
        button.className = classes;
        if (title) button.title = title;
        if (disabled) {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
        }
        button.addEventListener('mousedown', (event) => {
            event.stopPropagation();
        });
        button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (button.disabled) return;
            if (typeof onClick === 'function') {
                onClick();
            }
        });

        return button;
    };

    const addSlide = (component) => {
        const slidesCollection = getSlidesCollection(component);
        if (!slidesCollection) return;

        const slideHtml = `<div class="${options.classSlide || 'swiper-slide'}"></div>`;
        slidesCollection.add(slideHtml);
        const newSlideIndex = slidesCollection.length - 1;
        setKnownSlideIndex(component, newSlideIndex);

        setTimeout(() => {
            renderSlideTopBar(component);
            bindSwiperSlideListener(component);
            goToSlide(component, newSlideIndex);
        }, 80);
    };

    const removeActiveSlide = (component) => {
        const slidesCollection = getSlidesCollection(component);
        if (!slidesCollection || slidesCollection.length <= 1) return;

        const totalSlides = slidesCollection.length;
        const activeIndex = getKnownSlideIndex(component, totalSlides);
        const targetSlide = slidesCollection.at(activeIndex);
        if (!targetSlide) return;

        targetSlide.remove();
        const nextActiveIndex = Math.min(activeIndex, totalSlides - 2);
        setKnownSlideIndex(component, nextActiveIndex);

        setTimeout(() => {
            renderSlideTopBar(component);
            bindSwiperSlideListener(component);
            goToSlide(component, nextActiveIndex);
        }, 80);
    };

    const duplicateActiveSlide = (component) => {
        const slidesCollection = getSlidesCollection(component);
        if (!slidesCollection || !slidesCollection.length) return;

        const totalSlides = slidesCollection.length;
        const activeIndex = getKnownSlideIndex(component, totalSlides);
        const targetSlide = slidesCollection.at(activeIndex);
        if (!targetSlide) return;

        const cloned = targetSlide.clone();
        slidesCollection.add(cloned);
        const newSlideIndex = slidesCollection.length - 1;
        setKnownSlideIndex(component, newSlideIndex);

        setTimeout(() => {
            renderSlideTopBar(component);
            bindSwiperSlideListener(component);
            goToSlide(component, newSlideIndex);
        }, 80);
    };

    const moveActiveSlide = (component, direction) => {
        const slidesCollection = getSlidesCollection(component);
        if (!slidesCollection || slidesCollection.length <= 1) return;

        const totalSlides = slidesCollection.length;
        const activeIndex = getKnownSlideIndex(component, totalSlides);
        const targetIndex = activeIndex + direction;
        if (targetIndex < 0 || targetIndex >= totalSlides) return;

        const targetSlide = slidesCollection.at(activeIndex);
        if (!targetSlide) return;

        targetSlide.remove({ silent: true });
        slidesCollection.add(targetSlide, { at: targetIndex });
        setKnownSlideIndex(component, targetIndex);

        setTimeout(() => {
            renderSlideTopBar(component);
            bindSwiperSlideListener(component);
            goToSlide(component, targetIndex);
        }, 80);
    };

    const clearSelectedSlidesListener = () => {
        if (selectedSlidesCollection && selectedSlidesListener) {
            selectedSlidesCollection.off(
                'add remove reset sort',
                selectedSlidesListener
            );
        }
        selectedSlidesCollection = null;
        selectedSlidesListener = null;
    };

    const clearSwiperSlideListener = () => {
        if (selectedSwiperInstance && selectedSwiperSlideListener) {
            selectedSwiperInstance.off('slideChange', selectedSwiperSlideListener);
        }
        selectedSwiperInstance = null;
        selectedSwiperSlideListener = null;
    };

    const hideSlideTopBar = (component) => {
        const sliderEl = component?.getEl?.();
        if (sliderEl) {
            sliderEl.classList.remove(navUi.selectedClass);
            delete sliderEl.dataset.sioEditorActiveSlide;
        }
        const doc = sliderEl?.ownerDocument || topBarDoc;
        const topBar = doc?.querySelector(`.${navUi.barClass}`);
        if (topBar) topBar.remove();
        clearTopBarPositionListener();
    };

    const goToSlide = (component, index) => {
        const slideCount = getSlideCount(component);
        if (!slideCount) return;

        const targetIndex = setKnownSlideIndex(component, index);
        const swiper = getSwiperInstance(component);
        if (!swiper) return;

        if (swiper.params?.loop && typeof swiper.slideToLoop === 'function') {
            swiper.slideToLoop(targetIndex);
        } else if (typeof swiper.slideTo === 'function') {
            swiper.slideTo(targetIndex);
        }

        setTimeout(() => {
            renderSlideTopBar(component);
        }, 0);
    };

    const renderSlideTopBar = (component) => {
        if (!component || component.get('type') !== containerName) return;

        const sliderEl = component.getEl?.();
        if (!sliderEl) return;

        const slideCount = getSlideCount(component);
        if (!slideCount) {
            hideSlideTopBar(component);
            return;
        }

        const doc = sliderEl.ownerDocument;
        const win = doc.defaultView;

        // Topbar lives on the canvas body so the swiper container needs no overflow override
        let topBar = doc.querySelector(`.${navUi.barClass}`);
        if (!topBar) {
            topBar = doc.createElement('div');
            topBar.className = navUi.barClass;
            topBar.setAttribute('contenteditable', 'false');
            doc.body.appendChild(topBar);
        }

        topBar.innerHTML = '';
        sliderEl.classList.add(navUi.selectedClass);
        const activeIndex = getKnownSlideIndex(component, slideCount);
        const canMoveUp = activeIndex > 0;
        const canMoveDown = activeIndex < slideCount - 1;

        /* Left: Pagination pill */
        const paginationZone = doc.createElement('div');
        paginationZone.className = navUi.paginationClass;

        paginationZone.append(
            createNavButton(doc, {
                icon: icons.chevronLeft,
                classes: `${navUi.buttonClass} ${navUi.arrowButtonClass}`,
                title: 'Previous Slide',
                onClick: () => goToSlide(component, activeIndex - 1),
            })
        );

        const numbersContainer = doc.createElement('div');
        numbersContainer.className = navUi.numbersClass;
        for (let slideIndex = 0; slideIndex < slideCount; slideIndex += 1) {
            const isActive = slideIndex === activeIndex;
            numbersContainer.append(
                createNavButton(doc, {
                    label: `${slideIndex + 1}`,
                    classes: `${navUi.buttonClass} ${navUi.numberButtonClass}${isActive ? ` ${navUi.activeButtonClass}` : ''}`,
                    title: `Go to slide ${slideIndex + 1}`,
                    onClick: () => goToSlide(component, slideIndex),
                })
            );
        }

        paginationZone.append(numbersContainer);
        paginationZone.append(
            createNavButton(doc, {
                icon: icons.chevronRight,
                classes: `${navUi.buttonClass} ${navUi.arrowButtonClass}`,
                title: 'Next Slide',
                onClick: () => goToSlide(component, activeIndex + 1),
            })
        );

        topBar.append(paginationZone);

        const statusZone = doc.createElement('div');
        statusZone.className = navUi.statusClass;
        statusZone.textContent = ``;
        topBar.append(statusZone);

        const actionsZone = doc.createElement('div');
        actionsZone.className = navUi.actionsClass;

        actionsZone.append(
            createNavButton(doc, {
                icon: icons.arrowUp,
                classes: `${navUi.buttonClass} ${navUi.moveButtonClass}${canMoveUp ? '' : ` ${navUi.disabledButtonClass}`}`,
                title: canMoveUp ? 'Move Slide Up' : 'Current slide is already first',
                disabled: !canMoveUp,
                onClick: () => moveActiveSlide(component, -1),
            })
        );
        actionsZone.append(
            createNavButton(doc, {
                icon: icons.arrowDown,
                classes: `${navUi.buttonClass} ${navUi.moveButtonClass}${canMoveDown ? '' : ` ${navUi.disabledButtonClass}`}`,
                title: canMoveDown ? 'Move Slide Down' : 'Current slide is already last',
                disabled: !canMoveDown,
                onClick: () => moveActiveSlide(component, 1),
            })
        );
        actionsZone.append(
            createNavButton(doc, {
                icon: icons.duplicate,
                classes: `${navUi.buttonClass} ${navUi.controlButtonClass}`,
                title: 'Aktuelle Folie duplizieren',
                onClick: () => duplicateActiveSlide(component),
            })
        );
        actionsZone.append(
            createNavButton(doc, {
                icon: icons.plus,
                classes: `${navUi.buttonClass} ${navUi.controlButtonClass}`,
                title: 'Add Slide',
                onClick: () => addSlide(component),
            })
        );
        actionsZone.append(
            createNavButton(doc, {
                icon: icons.minus,
                classes: `${navUi.buttonClass} ${navUi.controlButtonClass}${slideCount > 1 ? '' : ` ${navUi.disabledButtonClass}`}`,
                title: slideCount > 1 ? 'Remove Active Slide' : 'At least one slide is required',
                disabled: slideCount <= 1,
                onClick: () => removeActiveSlide(component),
            })
        );

        topBar.append(actionsZone);

        positionTopBar(sliderEl, topBar);

        win?.requestAnimationFrame?.(() => {
            const activeBtn = numbersContainer.querySelector(`.${navUi.activeButtonClass}`);
            activeBtn?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
        });

        if (!topBarPositionListener || topBarDoc !== doc) {
            clearTopBarPositionListener();
            topBarDoc = doc;
            topBarPositionListener = () => positionTopBar(sliderEl, topBar);
            doc.addEventListener('scroll', topBarPositionListener, true);
            if (win) win.addEventListener('resize', topBarPositionListener);
        }
    };

    const bindSelectedSlidesListener = (component) => {
        clearSelectedSlidesListener();
        const wrapper = getSlidesWrapper(component);
        if (!wrapper) return;

        const slidesCollection = wrapper.components();
        const refresh = () => {
            setTimeout(() => {
                renderSlideTopBar(component);
            }, 0);
        };

        slidesCollection.on('add remove reset sort', refresh);
        selectedSlidesCollection = slidesCollection;
        selectedSlidesListener = refresh;
    };

    const bindSwiperSlideListener = (component) => {
        clearSwiperSlideListener();
        const swiper = getSwiperInstance(component);
        if (!swiper || typeof swiper.on !== 'function') return;

        const refresh = () => {
            const totalSlides = getSlideCount(component);
            const slideIndex = getCurrentSlideIndex(component, totalSlides);
            setKnownSlideIndex(component, slideIndex);
            renderSlideTopBar(component);
        };

        swiper.on('slideChange', refresh);
        selectedSwiperInstance = swiper;
        selectedSwiperSlideListener = refresh;
    };

    const activateSwiperSelection = (component) => {
        if (selectedSwiperComponent && selectedSwiperComponent !== component) {
            hideSlideTopBar(selectedSwiperComponent);
        }

        selectedSwiperComponent = component;
        setKnownSlideIndex(component, getCurrentSlideIndex(component, getSlideCount(component)));
        renderSlideTopBar(component);
        bindSelectedSlidesListener(component);
        bindSwiperSlideListener(component);

        setTimeout(() => {
            renderSlideTopBar(component);
            bindSwiperSlideListener(component);
        }, 75);
    };

    const deactivateSwiperSelection = (component) => {
        if (!component || component.get('type') !== containerName) return;
        hideSlideTopBar(component);

        if (selectedSwiperComponent === component) {
            selectedSwiperComponent = null;
            selectedSlideIndex = 0;
            clearSelectedSlidesListener();
            clearSwiperSlideListener();
        }
    };

    // Add components
    loadComponents(editor, options);

    // Add blocks
    loadBlocks(editor, options);

    editor.Commands.add('swiper:add-slide', {
        run(editor, sender, cmdOptions) {
            const selected = editor.getSelected();
            if (selected && selected.get('type') === containerName) {
                addSlide(selected);
            }
        }
    });

    editor.on('component:selected', (component) => {
        if (component.get('type') === containerName) {
            // Hide default gjs toolbar when slider is in focus (custom topbar is used instead)
            // component.set('toolbar', []);

            activateSwiperSelection(component);
            return;
        }

        if (selectedSwiperComponent) {
            deactivateSwiperSelection(selectedSwiperComponent);
        }
    });

    editor.on('component:deselected', (component) => {
        deactivateSwiperSelection(component);
    });

    editor.on('component:mount', (component) => {
        if (selectedSwiperComponent && component === selectedSwiperComponent) {
            setTimeout(() => {
                renderSlideTopBar(component);
                bindSwiperSlideListener(component);
            }, 0);
        }
    });

    editor.on('component:remove', (component) => {
        if (component === selectedSwiperComponent) {
            selectedSlideIndex = 0;
            clearSelectedSlidesListener();
            clearSwiperSlideListener();
            selectedSwiperComponent = null;
        }
    });
}
